import React, { useState, useEffect, useContext } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ExpandableTable.css";

import { useTranslation } from "react-i18next";
import i18n from "i18next";
import SvgSortdownarrow from "../../../../Components/IconComponents/Sortdownarrow";
import SvgSortuparrow from "../../../../Components/IconComponents/Sortuparrow";
import SvgSorting from "../../../../Components/IconComponents/Sortingarrow";
import { getReleaseResourceDeploy, getResourceDeploy, vehiclerelease } from "../../../../Services/EndPoints";
import { getDataById, putDataWithBody, getData } from "../../../../Services/Services";
import { Button, Modal } from "react-bootstrap";
import { XCircleblack } from "../../../../Components/IconComponents";
import { ThemeContext } from "../../../..";
import TableHeader from "../../../../Components/UiComponents/Table/TableHeader/TableHeader";
import { toast, ToastContainer } from "react-toastify";
import { Tooltip, OverlayTrigger } from "react-bootstrap";

// Import the ResourceReleaseIcon and required services from DeploymentList
const ResourceReleaseIconOpen = ({ className, onClick }) => (
  <svg
    className={className}
    onClick={onClick}
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ cursor: 'pointer' }}
  >
    {/* Completely open shackle - detached U-shape to the left */}
    <path
      d="M2 8V6a4 4 0 018 0v2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />

    {/* Lock body */}
    <rect
      x="4"
      y="11"
      width="16"
      height="10"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />

    {/* Keyhole */}
    <circle cx="12" cy="16" r="1" fill="currentColor" />
  </svg>
);
const ResourceReleaseIconClose = ({ className, onClick }) => (
  <svg
    className={className}
    onClick={onClick}
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ cursor: 'pointer' }}
  >
    {/* Closed shackle */}
    <path
      d="M8 11V7a4 4 0 118 0v4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />

    {/* Lock body */}
    <rect
      x="4"
      y="11"
      width="16"
      height="10"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />

    {/* Keyhole */}
    <circle cx="12" cy="16" r="1" fill="currentColor" />
  </svg>
);

// Default filter UI for a column
const DefaultColumnFilter = ({
  column: { filterValue, preFilteredRows, setFilter },
}) => {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      placeholder={`Filter...`}
      style={{
        width: "100%",
        padding: "5px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "5px",
      }}
    />
  );
};

const DeploymentListExpandableTable = (props) => {
  const {
    columns,
    data,
    numberOfRows,
    headerTableHeadStyle = {},
    tableStyle = {},
    showRowCount = true,
    rowCount,
    tabChanger = false,
    activeTab,
    setActiveTab,
    searchData,
    setSearchData = () => { },
    enableColumnFilters = false,
    disbalePaginationFilter = false,
    disbalePaginationFilter1 = false,
    disbalePaginationFilter2 = false,
    // Add these props for release functionality
    onResourceRelease,
    loader = false,
    onDataRefresh,
    highlightedIncidentId,
    highlightDeployIds = [],
  } = props;

  const highlightDeploySet = (highlightDeployIds || []).map((v) => String(v));
  const rowHasDeployMatch = (rowData) =>
    highlightDeploySet.length > 0 &&
    (rowData?.deployed_resource_list || []).some((d) =>
      highlightDeploySet.includes(String(d.id))
    );

  const [expandedRowIds, setExpandedRowIds] = useState({});
  const { theme } = useContext(ThemeContext);
  const [deployListTable, setDeployListTable] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [isReleasing, setIsReleasing] = useState(false);
  // Add these states for release modal
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [selectedResourceForRelease, setSelectedResourceForRelease] = useState(null);

  const handleRegionClick = (region) => {
    setSelectedRegion(region);
    setShowRegionModal(true);
  };

  const handleRegionModalClose = () => {
    setShowRegionModal(false);
    setSelectedRegion("");
  };

  const refreshResourceDeployList = async () => {
    try {
      const viewData = await getData(getResourceDeploy);
      const processedData = viewData.map((item, index) => ({
        ...item,
        id: index,
        deployedResourceList: item.deployed_resource_list || []
      }));

      // If parent component has a callback to update data, use it
      if (onDataRefresh) {
        onDataRefresh(processedData);
      }

      return processedData;
    } catch (error) {
      console.error("Error fetching resource data:", error);
      toast.error("Failed to refresh resource data", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      throw error;
    }
  };
  // Add release functionality
  const handleReleaseClick = (resourceData) => {
    setSelectedResourceForRelease(resourceData);
    setShowReleaseModal(true);
  };

  const handleRelease = async () => {
    if (selectedResourceForRelease) {
      try {
        const requestData = [{
          resourceId: selectedResourceForRelease.resource_id,
          id: selectedResourceForRelease.selectedDeployment?.id || selectedResourceForRelease.id,
        }];

        const response = await putDataWithBody(getReleaseResourceDeploy, requestData, {});

        if (response.status === "success" || response.status === "multi-status") {
          const allSuccessful = response.results?.every(r => r.status === "success");

          if (allSuccessful) {
            const vehicleDeployIds = (selectedResourceForRelease.selectedDeployment?.vehicles || [])
              .filter(vehicle => vehicle.vehicle_deploy_id && vehicle.is_deployed)
              .map(vehicle => vehicle.vehicle_deploy_id);

            // Only call vehicle release if there are vehicle IDs
            if (vehicleDeployIds.length > 0) {
              try {
                await putDataWithBody(vehiclerelease, vehicleDeployIds, {});
              } catch (vehicleError) {
                console.error("Error releasing vehicles:", vehicleError);
              }
            }
            toast.success("Resource released successfully", {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000,
            });
          } else {
            const failed = response.results?.filter(r => r.status !== "success");
            toast.error(`Some resources failed to release: ${failed.map(f => f.msg).join(', ')}`, {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 3000,
            });
          }

          onDataRefresh();
        } else {
          toast.error(response.msg || "Failed to release resource", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Error releasing resource:", error);
        toast.error("An error occurred while releasing the resource", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      } finally {
        // Optional loader handling can go here
      }

      setShowReleaseModal(false);
      setSelectedResourceForRelease(null);
    }
  };




  const handleReleaseCancel = () => {
    setShowReleaseModal(false);
    setSelectedResourceForRelease(null);
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRowIds((prev) => {
      const isCurrentlyExpanded = !!prev[rowId];
      if (!isCurrentlyExpanded) {
        setExpandedRowId(rowId);
      } else {
        setExpandedRowId(null);
      }
      return isCurrentlyExpanded ? {} : { [rowId]: true };
    });
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    rows,
    state: { pageIndex, globalFilter, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: { pageIndex: 0, pageSize: numberOfRows },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { t } = useTranslation();

  const translateNumber = (number) => {
    const numberString = number.toString();
    return i18n.t(`numbers.${numberString}`, { defaultValue: numberString });
  };

  let tempRowCount = globalFilter ? rows.length : rowCount || data.length;
  let tempPageIndex = pageIndex;
  let tempPageSize = pageSize;

  if (tempPageIndex === undefined || tempPageIndex === null) {
    tempPageIndex = 0;
  }
  if (tempRowCount === undefined || tempRowCount === null) {
    tempRowCount = 0;
  }
  if (tempPageSize === undefined || tempPageSize === null) {
    tempPageSize = 10;
  }

  const startIndex = tempPageIndex * tempPageSize + 1 || 1;
  const endIndex =
    Math.min((tempPageIndex + 1) * tempPageSize, tempRowCount) || tempRowCount;

  const translatedStartIndex = startIndex;
  const translatedEndIndex = endIndex;
  const translatedRowCount = tempRowCount;

  useEffect(() => {
    setPageSize(numberOfRows);
  }, [numberOfRows]);

  useEffect(() => {
    if (globalFilter) {
      const pageData = page?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    } else {
      const pageData = rows?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    }
  }, [globalFilter, rows]);

  const renderExpandedRow = (row) => {
    const deployedResourceList = row.original.deployed_resource_list || [];

    return (
      <tr>
        <td colSpan="10">
          <div
            style={{
              // width: "85%",
              border: "1px solid white",
              borderRadius: "10px",
              marginTop: "10px",
              marginLeft: "16px",
              marginRight: "16px",
              marginBottom: "10px",
              border: "1px solid #ddd",
            }}
          >
            <table
              style={{
                height: "5%",
                borderCollapse: "collapse",
                position: "relative",
                marginBottom: "-1px"
              }}
            >
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr style={{ backgroundColor: "#18a3a0" }}>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Resource Name")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Deployed Facility Name")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "center",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Deployed Quantity")}
                  </th>
                  {/* <th
                    style={{
                      padding: "8px",
                      textAlign: "center",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Deployed Quantity Updated By")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "center",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Deployed Quantity Updated Date & Time")}
                  </th> */}
                  <th
  style={{
    padding: "8px",
    textAlign: "left",
    borderBottom: "1px solid #ddd",
    color: "white",
    verticalAlign: "Top"
  }}
>
  {t("Deployment Type")}
</th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Deployed Date and Time")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Released Date and Time")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Deployed By")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Released By")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Status")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderBottom: "1px solid #ddd",
                      color: "white",
                      verticalAlign: "Top"
                    }}
                  >
                    {t("Action")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {deployedResourceList.length > 0 ? (
                  deployedResourceList.map((deploy, index) => {
                    const isDeployMatch =
                      highlightDeploySet.length > 0 &&
                      highlightDeploySet.includes(String(deploy.id));
                    return (
                    <tr
                      key={deploy.id || index}
                      style={isDeployMatch ? { background: "#e2e8f0", fontWeight: 700, color: "#1e293b" } : {}}
                    >
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        {deploy.resource_name || t("NA")}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        {deploy.deploy_facility_name || t("NA")}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        {deploy.deploy_quantity || t("NA")}
                      </td>
                      {/* ── NEW: Updated By ── */}
                      {/* <td style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid gray" }}>
                        {deploy.updated_by || t("NA")}
                      </td> */}
                      {/* ── NEW: Updated Date & Time ── */}
                      {/* <td style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid gray" }}>
                        {deploy.updated_on
                          ? new Date(deploy.updated_on).toLocaleDateString('en-GB') +
                            ', ' +
                            new Date(deploy.updated_on).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : t("NA")}
                      </td> */}
                      <td
  style={{
    padding: "8px",
    textAlign: "left",
    borderBottom: "1px solid gray",
  }}
>
  {deploy.is_auto_triggered ? "Automatic" : "Manual"}
</td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        {deploy.deployed_date_and_time
                          ? new Date(deploy.deployed_date_and_time).toLocaleDateString('en-GB') + ', ' +
                          new Date(deploy.deployed_date_and_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : t("NA")
                        }
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        {deploy.released_date_and_time
                          ? new Date(deploy.released_date_and_time).toLocaleDateString('en-GB') + ', ' +
                          new Date(deploy.released_date_and_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : t("NA")
                        }
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        {deploy.deployed_by || t("NA")}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        {deploy.released_by || t("NA")}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: deploy.is_deployed ? '#ff4d4f' : '#52c41a',
                            }}
                          />
                          <span>{deploy.is_deployed ? "Deployed" : "Released"}</span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "1px solid gray",
                        }}
                      >
                        {deploy.is_deployed ? (
                          <div className="usertable__actionsicon">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="release-tooltip">
                                  Click to Release
                                </Tooltip>
                              }
                            >
                              <span onClick={() => handleReleaseClick({
                                ...row.original,
                                selectedDeployment: deploy
                              })}>
                                <ResourceReleaseIconClose className="cursor-pointer ms-3" />
                              </span>
                            </OverlayTrigger>
                          </div>
                        ) : (
                          <div className="usertable__actionsicon">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="released-tooltip">
                                  Resource Already Released
                                </Tooltip>
                              }
                            >
                              <span style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                                <ResourceReleaseIconOpen className="ms-3" disabled={true} />
                              </span>
                            </OverlayTrigger>
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                      {t("No deployed resources found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="tabl_container">
      <TableHeader
        {...props}
        searchValue={globalFilter || ""}
        onSearchHandler={setGlobalFilter}
      />
      <div className="iccc__table--container" style={tableStyle}>
        <table {...getTableProps()} className="table iccc--table">
          <thead>
            <tr>
              <th
                style={{
                  width: "30px",
                  textAlign: "left",
                  color: "white",
                  backgroundColor: "#18a3a0",
                }}
              >
                {t("")}
              </th>
              {headerGroups.map((headerGroup, index) => (
                <React.Fragment key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={headerTableHeadStyle}
                      key={index}
                    >
                      {column.render("Header")}
                      {!column.disableSortBy &&
                        (column.isSorted ? (
                          column.isSortedDesc ? (
                            <SvgSortdownarrow />
                          ) : (
                            <SvgSortuparrow />
                          )
                        ) : (
                          <SvgSorting />
                        ))}
                      {enableColumnFilters && column.canFilter && (
                        <div>{column.render("Filter")}</div>
                      )}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row, i) => {
                prepareRow(row);
                const isExpanded = expandedRowIds[row.id];
                const isHighlighted =
                  (highlightedIncidentId &&
                    String(row.original.incident_id) === String(highlightedIncidentId)) ||
                  rowHasDeployMatch(row.original);

                const highlightCellStyle = isHighlighted
                  ? { background: "#e2e8f0", fontWeight: "700", color: "#1e293b" }
                  : {};

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      {...row.getRowProps()}
                      style={isHighlighted ? { borderLeft: "3px solid #475569" } : {}}
                    >
                      <td style={{ textAlign: "left", ...highlightCellStyle }}>
                        <span
                          onClick={() => toggleRowExpansion(row.id)}
                          style={{ cursor: "pointer", fontSize: "18px" }}
                        >
                          {isExpanded ? "-" : "+"}
                        </span>
                      </td>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id}
                          style={{ textAlign: "left", ...highlightCellStyle }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && renderExpandedRow(row)}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="no-data-text">
                  No data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Region Modal */}
        <Modal
          show={showRegionModal}
          onHide={handleRegionModalClose}
          dialogClassName="custom-modal"
          centered
          style={{
            borderRadius: "20px",
            border: "none",
            top: "20%",
          }}
        >
          <Modal.Header
            style={{
              backgroundColor: theme === "light" ? "#2b6591" : "#1c2038",
              color: "white",
              borderBottom: "none",
            }}
          >
            <Modal.Title style={{ color: "white" }}>Region Details</Modal.Title>
            <XCircleblack
              onClick={handleRegionModalClose}
              width="1.5em"
              height="1.5em"
              className="cursor-pointer"
            />
          </Modal.Header>
          <Modal.Body
            style={{
              backgroundColor: theme === "light" ? "#fcfcfc" : '#252b48',
              color: theme === "light" ? "#001b30" : 'white',
              border: "none",
              padding: "20px",
              lineHeight: "1.8",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            <p>{selectedRegion}</p>
          </Modal.Body>
          <Modal.Footer
            style={{
              backgroundColor: theme === "light" ? "#fcfcfc" : "#252b48",
              color: "white",
              borderTop: "none",
            }}
          >
            <Button
              variant="secondary"
              onClick={handleRegionModalClose}
              style={{
                backgroundColor: theme === "light" ? "#2b6591" : "#252b48",
                borderColor: "#003366",
                borderRadius: "5px",
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Release Confirmation Modal */}
        <Modal
          show={showReleaseModal}
          onHide={handleReleaseCancel}
          style={{ marginTop: '6rem' }}
          dialogClassName="custom-modal-width"
        >
          <Modal.Body>
            Are you sure you want to release this resource?
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <div className="d-flex gap-2" style={{ marginTop: "-2rem" }}>
              <Button className="btn-cancel" onClick={handleReleaseCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleRelease} disabled={loader}>
                {loader ? "Releasing..." : "Release"}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>

      <div className="d-flex justify-content-between align-items-center">
        {!disbalePaginationFilter2 && (
          <div className="float-start m-2 mt-2 ms-3">
            {showRowCount && (
              <div className="float-start m-2 mt-1 ms-3 pagecount-style">
                {t("showingentriestable.showing")} {translatedStartIndex}{" "}
                {t("showingentriestable.to")} {translatedEndIndex}{" "}
                {t("showingentriestable.of")} {translatedRowCount}{" "}
                {t("showingentriestable.entries")}
              </div>
            )}
          </div>
        )}
        <div className="pagination">
          {!disbalePaginationFilter && (
            <div className="d-flex">
              <span className="pagination_gotopage">
                Go to Page:
                <input
                  type="number"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    const pageNumber = e.target.value
                      ? Number(e.target.value) - 1
                      : 0;
                    gotoPage(pageNumber);
                  }}
                  className="pagination_gotopage-input"
                />
              </span>
              <span>
                <select
                  className="pagination_select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 50, 100, 500, 1000, 2000].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      show {pageSize}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          )}
          {!disbalePaginationFilter1 && (
            <>
              <button
                className="pagebtn mx-2"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {"<"}
              </button>
              <button className="pagination_page">
                {translateNumber(pageIndex + 1)}{" "}
              </button>
              <button
                className="pagebtn"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                {">"}
              </button>{" "}
              <span className="right_pageno">
                {t("pagination.page_of", {
                  page: translateNumber(pageIndex + 1),
                  total: translateNumber(pageOptions.length),
                })}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeploymentListExpandableTable;
